import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { memoize } from '../../../../core/decorators/memoize';
import { CCLocation } from '../../../../models/locations-models/cc-location';
import { GbpLocationGroup } from '../../../../models/locations-models/gbp-location-group';
import { CC_OPERATIONS, CCRowSetting } from '../../../../models/locations-models/cc-row-setting';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Dictionary } from '@ngrx/entity';
import { TypedSimpleChanges } from '../../../../utils/typescript';
import { CCOperationActions } from '../state/cc-list-actions';
import { selectCCActionButtonsViewModel } from '../state/cc-list-combined-selectors';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { GtagEventDirective } from '../../../../core/gx-gtag/gtag-event.directive';
import { MatIconButton } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-cc-action-buttons',
    templateUrl: './cc-action-buttons.component.html',
    styleUrls: ['./cc-action-buttons.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        MatIconButton,
        GtagEventDirective,
        MatIcon,
        MatTooltip,
        MatProgressSpinner,
        NgxSkeletonLoaderModule,
    ],
})
export class CCActionButtonsComponent implements OnChanges {

  @Input() placeId: string;

  ccActionButtonsViewModel$: Observable<{
    ccMenuRowViewModel: {
      focusedLocationId: number,
      ccRowSettingsDictionary: Dictionary<CCRowSetting>,
      ccLocationGroup: GbpLocationGroup,
      ccLocationsLoading: boolean
    },
    ccLocation: CCLocation
  }>;

  ccOperations = CC_OPERATIONS;

  constructor(private store: Store) {
  }

  ngOnChanges(changes: TypedSimpleChanges<CCActionButtonsComponent>) {
    if (changes.placeId.currentValue) {
      this.ccActionButtonsViewModel$ = this.store.select(selectCCActionButtonsViewModel(changes.placeId.currentValue))
    }
  }


  /**
   * gets the correct tooltip for the claim/undo claim button
   * @param ccLocation
   * @param ccLocationGroup
   */
  @memoize()
  getClaimButtonTooltip(ccLocation: CCLocation, ccLocationGroup: GbpLocationGroup): string {
    return !ccLocationGroup ? 'Select a valid GBP location group first' :
        ccLocation.isClaimCandidate ?
            'Claim this Google Maps location for the above LSC location' :
            ccLocation.isUnclaimCandidate ?
                'Undo claiming by removing the GBP location' :
                ccLocation.isMarkedAsDuplicate ?
                    'A Google Maps location marked as duplicate can\'t be claimed' :
                    ccLocation.isClaimed && !ccLocation.isClaimedByTheFocusedLocation ?
                        'This Google Maps location is already claimed for another LSC location' :
                        'You cannot claim multiple Google Maps locations per LSC location';
  }

  /**
   * gets the correct tooltip for the mark/undo mark as dupe button
   * @param ccLocation
   * @param ccLocationGroup
   */
  @memoize()
  getDuplicateButtonTooltip(ccLocation: CCLocation, ccLocationGroup: GbpLocationGroup): string {
    return !ccLocationGroup ? 'Select a valid GBP location group first' :
        ccLocation.isMarkedAsDuplicate ?
            'Undo mark Google Maps location as a duplicate' :
            ccLocation.isMarkAsDuplicateCandidate ?
                'Mark Google Maps location as a duplicate' :
                ccLocation.isClaimed ?
                    'A claimed Google Maps location cannot be marked as a duplicate' :
                    'The LSC location needs to be claimed first in order to mark duplicates';
  }

  startCcOperation(focusedLocationId: number, ccRowSetting: CCRowSetting, ccOperation: CC_OPERATIONS, starredState?: boolean) {
    const ccRowSettingCopy = { ...ccRowSetting };
    ccRowSettingCopy.ongoingCcOperation = ccOperation;
    this.store.dispatch(CCOperationActions.ccOperation({
      focusedLocationId,
      ccRowSetting: ccRowSettingCopy,
      ccOperation,
      starredState,
    }));
  }

}
